/**
 * /sign-in — Surface Layer (/01)
 *
 * Passwordless-first login for seeded/invited accounts (private beta — no sign-up):
 *   1. Passkey (Face ID / Touch ID) — the primary path, usernameless.
 *   2. Email sign-in link (magic-link) — enrollment + new-device fallback.
 *   3. Password — retained fallback for seeded accounts.
 * Carbon Form primitives in a g100-fixed theme; zero inline styles.
 */

import { useEffect, useState, type FormEvent } from 'react';
import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import {
  Button,
  Form,
  FormGroup,
  InlineNotification,
  Link as CarbonLink,
  PasswordInput,
  Stack,
  TextInput,
  Theme,
} from '@carbon/react';
import { Login } from '@carbon/icons-react';
import { useSurfaceSession } from '@auth/useSurfaceSession';
import { useAuthStore } from '@auth/auth-store';
import { sendMagicLink } from '@auth-provider';
import { useWebauthn, isPasskeySupported } from '@lib/auth/useWebauthn';
import './sign-in.scss';

export const Route = createFileRoute('/sign-in')({
  beforeLoad: () => {
    if (useAuthStore.getState().identity) {
      throw redirect({ to: '/dashboard' });
    }
  },
  component: SignInPage,
});

export function SignInPage() {
  const navigate = useNavigate();
  const { signIn, error, configured, status } = useSurfaceSession();
  const { busy: passkeyBusy, error: passkeyError, login: passkeyLogin } = useWebauthn();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [linkBusy, setLinkBusy] = useState(false);
  const [linkSent, setLinkSent] = useState(false);
  const [linkError, setLinkError] = useState<string | null>(null);
  const passkeySupported = isPasskeySupported();

  useEffect(() => {
    if (status === 'authenticated') void navigate({ to: '/dashboard' });
  }, [status, navigate]);

  async function handlePasskey() {
    const ok = await passkeyLogin();
    if (ok) void navigate({ to: '/dashboard' });
  }

  const [linkUnregistered, setLinkUnregistered] = useState(false);

  async function handleMagicLink() {
    if (!email.trim() || linkBusy) return;
    setLinkBusy(true);
    setLinkSent(false);
    setLinkUnregistered(false);
    try {
      const result = await sendMagicLink(email.trim());
      if (!result.error) {
        setLinkSent(true);
        setLinkError(null);
      } else if (/signup|sign-up|not allowed|not found/i.test(result.error.message)) {
        // Private beta: sign-in links exist only for pre-registered e-mails
        // (operator decision 2026-06-10 — explicit message over anti-enumeration).
        setLinkUnregistered(true);
        setLinkError(null);
      } else {
        setLinkError(result.error.message);
      }
    } finally {
      setLinkBusy(false);
    }
  }

  async function handlePassword(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      const result = await signIn(email.trim(), password);
      if (result.ok) void navigate({ to: '/dashboard' });
    } finally {
      setSubmitting(false);
    }
  }

  const errorSubtitle =
    error?.code === 'tenant_context_missing'
      ? 'Signed in — your workspace is being provisioned. Try again shortly.'
      : error?.message ?? 'Email or password is incorrect.';

  return (
    <Theme theme="g100">
      <main className="sign-in-page">
        <section className="sign-in-card" aria-labelledby="sign-in-heading">
          <Login size={32} aria-hidden="true" className="sign-in-card__icon" />
          <h1 id="sign-in-heading" className="sign-in-card__heading">
            Sign in to Workspace
          </h1>
          <p className="sign-in-card__subtitle">
            Private beta — unlock with your passkey (Face ID / Touch ID), or get a one-time sign-in link by email.
          </p>

          {error ? (
            <InlineNotification
              kind={error.code === 'tenant_context_missing' ? 'warning' : 'error'}
              lowContrast
              hideCloseButton
              title={error.code === 'tenant_context_missing' ? 'Workspace provisioning' : 'Sign-in failed'}
              subtitle={errorSubtitle}
            />
          ) : !configured ? (
            <InlineNotification
              kind="warning"
              lowContrast
              hideCloseButton
              title="Local design mode"
              subtitle="VITE_SUPABASE_* not configured — using a local preview session."
            />
          ) : null}

          {passkeyError ? (
            <InlineNotification
              kind="error"
              lowContrast
              hideCloseButton
              title="Passkey"
              subtitle={`${passkeyError.message} (ref ${passkeyError.correlationId})`}
            />
          ) : null}
          {linkSent ? (
            <InlineNotification
              kind="success"
              lowContrast
              hideCloseButton
              title="Check your email"
              subtitle={`We sent a one-time sign-in link to ${email.trim()}.`}
            />
          ) : null}
          {linkUnregistered ? (
            <InlineNotification
              kind="warning"
              lowContrast
              hideCloseButton
              title="E-mail not registered"
              subtitle="This e-mail is not in the workspace database. Open an access request on the support page."
            />
          ) : null}
          {linkError ? (
            <InlineNotification kind="error" lowContrast hideCloseButton title="Sign-in link" subtitle={linkError} />
          ) : null}

          <Stack gap={6}>
            {passkeySupported && configured ? (
              <Button
                kind="primary"
                renderIcon={Login}
                disabled={passkeyBusy}
                onClick={() => void handlePasskey()}
              >
                {passkeyBusy ? 'Waiting for passkey…' : 'Sign in with passkey'}
              </Button>
            ) : null}

            <Form aria-label="Email sign-in link" onSubmit={(event) => { event.preventDefault(); void handleMagicLink(); }}>
              <Stack gap={5}>
                <FormGroup legendText="First time, or no passkey on this device?">
                  <TextInput
                    id="sign-in-email"
                    type="email"
                    labelText="Email"
                    helperText="We email a one-time link; opening it sets your password and passkey."
                    autoComplete="username webauthn"
                    required
                    value={email}
                    onChange={(event) => setEmail(event.currentTarget.value)}
                  />
                </FormGroup>
                <Button type="submit" kind="tertiary" disabled={linkBusy || !email.trim()}>
                  {linkBusy ? 'Sending…' : 'Email me a sign-in link'}
                </Button>
              </Stack>
            </Form>

            {showPassword ? (
              <Form aria-label="Password sign-in" onSubmit={handlePassword}>
                <Stack gap={5}>
                  <FormGroup legendText="">
                    <PasswordInput
                      id="sign-in-password"
                      labelText="Password (verification entry)"
                      helperText="Password sign-in does not unlock the workspace — you'll confirm by e-mail."
                      autoComplete="current-password"
                      value={password}
                      onChange={(event) => setPassword(event.currentTarget.value)}
                    />
                  </FormGroup>
                  <Button type="submit" kind="ghost" disabled={submitting || !password}>
                    {submitting ? 'Verifying…' : 'Continue with password'}
                  </Button>
                </Stack>
              </Form>
            ) : (
              <Button kind="ghost" size="sm" onClick={() => setShowPassword(true)}>
                Use password instead
              </Button>
            )}

            <CarbonLink href="/support">Not registered? Open a support request</CarbonLink>
          </Stack>
        </section>
      </main>
    </Theme>
  );
}
